<template>
  <AppLayout title="Mein Konto">
    <div class="min-h-screen bg-gray-50">
      <!-- Header -->
      <div class="bg-white shadow">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div class="flex justify-between items-center">
            <div>
              <h1 class="text-2xl font-bold text-gray-900">{{ $t('Mein Konto') }}</h1>
              <p class="text-sm text-gray-600 mt-1">{{ $t('Verwalten Sie Ihre Kundendaten und Adressen') }}</p>
            </div>
            <div class="flex space-x-3">
              <Link 
                :href="route('userAccount')" 
                class="bg-stone-600 text-white px-4 py-2 rounded-lg hover:bg-stone-700 transition-colors"
              >
                {{ $t('Account & Passwort') }}
              </Link>
              <Link 
                :href="route('ordersList')" 
                class="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors"
              >
                {{ $t('Meine Bestellungen') }}
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <!-- Success Message -->
        <div v-if="$page.props.flash?.msg?.type === 'success'" class="mb-6 bg-green-50 border-l-4 border-green-400 p-4">
          <div class="flex">
            <div class="flex-shrink-0">
              <svg class="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
              </svg>
            </div>
            <div class="ml-3">
              <p class="text-sm text-green-700">{{ $page.props.flash.msg.msg }}</p>
            </div>
          </div>
        </div>

        <!-- Missing Store Address Warning -->
        <div v-if="missingStoreAddressHint" class="mb-6 bg-orange-50 border-l-4 border-orange-400 p-4">
          <div class="flex">
            <div class="flex-shrink-0">
              <svg class="h-5 w-5 text-orange-400" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
              </svg>
            </div>
            <div class="ml-3">
              <p class="text-sm text-orange-700">
                <strong>{{ $t('Bitte vervollständigen Sie die Adressinformationen und Öffnungszeiten für mindestens ein Geschäft.') }}</strong>
              </p>
            </div>
          </div>
        </div>

        <!-- Overview / Statistics -->
        <div v-if="statistics" class="mb-8">
          <h2 class="text-lg font-semibold text-gray-900 mb-4">{{ $t('Übersicht') }}</h2>
          <div class="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <!-- YTD order count -->
            <div class="bg-white rounded-lg shadow border border-gray-100 p-4">
              <div class="text-sm text-gray-500">
                {{ $t('Bestellungen') }} {{ statistics.year }}
              </div>
              <div class="mt-1 text-2xl font-semibold text-gray-900">
                {{ statistics.ytdCount }}
              </div>
              <div class="mt-1 text-xs text-gray-500">
                {{ $t('seit dem 1. Januar') }}
              </div>
            </div>

            <!-- YTD net sum -->
            <div class="bg-white rounded-lg shadow border border-gray-100 p-4">
              <div class="text-sm text-gray-500">
                {{ $t('Umsatz') }} {{ statistics.year }} ({{ $t('netto') }})
              </div>
              <div class="mt-1 text-2xl font-semibold text-gray-900">
                {{ formatMoney(statistics.ytdNet) }}
              </div>
              <div class="mt-1 text-xs text-gray-500">
                {{ $t('Brutto') }}: {{ formatMoney(statistics.ytdGross) }}
              </div>
            </div>

            <!-- Lifetime net sum -->
            <div class="bg-white rounded-lg shadow border border-gray-100 p-4">
              <div class="text-sm text-gray-500">
                {{ $t('Gesamtumsatz') }}
              </div>
              <div class="mt-1 text-2xl font-semibold text-gray-900">
                {{ formatMoney(statistics.lifetimeNet) }}
              </div>
              <div class="mt-1 text-xs text-gray-500">
                {{ statistics.lifetimeCount }} {{ $t('Bestellungen insgesamt') }}
              </div>
            </div>

            <!-- AOV -->
            <div class="bg-white rounded-lg shadow border border-gray-100 p-4">
              <div class="text-sm text-gray-500">
                {{ $t('Ø Bestellwert') }}
              </div>
              <div class="mt-1 text-2xl font-semibold text-gray-900">
                {{ statistics.aov === null ? '—' : formatMoney(statistics.aov) }}
              </div>
              <div class="mt-1 text-xs text-gray-500">
                {{ $t('Durchschnitt brutto') }}
              </div>
            </div>
          </div>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <!-- Customer Information -->
          <div class="lg:col-span-2 space-y-6">
            <!-- Main Information Card -->
            <div class="bg-white shadow rounded-lg">
              <div class="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                <h2 class="text-lg font-semibold text-gray-900">{{ $t('Meine Hauptinformationen') }}</h2>
                <Link
                  :href="route('customer.edit')"
                  class="btn btn-sonnenglas-lite btn-small"
                >
                  {{ $t('Bearbeiten') }}
                </Link>
              </div>
              <div class="p-6">
                <dl class="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <dt class="text-sm font-medium text-gray-500">{{ $t('Kundenname') }}</dt>
                    <dd class="mt-1 text-sm text-gray-900">{{ customer.name }}</dd>
                  </div>

                  <div>
                    <dt class="text-sm font-medium text-gray-500">{{ $t('Kundennummer') }}</dt>
                    <dd class="mt-1 text-sm text-gray-900">{{ customer.customer_number }}</dd>
                  </div>

                  <div>
                    <dt class="text-sm font-medium text-gray-500">{{ $t('Land') }}</dt>
                    <dd class="mt-1 text-sm text-gray-900">{{ getCountryName(customer.country) }}</dd>
                  </div>

                  <div>
                    <dt class="text-sm font-medium text-gray-500">{{ $t('USt-IdNr.') }}</dt>
                    <dd class="mt-1 text-sm text-gray-900">
                      {{ customer.vat_id || $t('Keine USt-IdNr. angegeben.') }}
                    </dd>
                  </div>

                  <div class="sm:col-span-2">
                    <dt class="text-sm font-medium text-gray-500">{{ $t('Rechnungs-E-Mail') }}</dt>
                    <dd class="mt-1">
                      <span
                        v-if="customer.email"
                        class="text-sm text-gray-900"
                      >
                        {{ customer.email }}
                      </span>
                      <span
                        v-else
                        class="text-sm text-red-600 font-bold"
                      >
                        {{ $t('Fehlende Rechnungs-E-Mail-Adresse') }}
                      </span>
                    </dd>
                  </div>
                </dl>
              </div>
            </div>

            <!-- Addresses Card -->
            <div class="bg-white shadow rounded-lg">
              <div class="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                <h2 class="text-lg font-semibold text-gray-900">{{ $t('Adressen') }}</h2>
                <Link
                  :href="route('createAddress')"
                  class="btn btn-sonnenglas"
                >
                  {{ $t('Neue Adresse erstellen') }}
                </Link>
              </div>
              <div class="p-6">
                <div class="space-y-6">
                  <div 
                    v-for="address in customer.sorted_addresses" 
                    :key="address.id" 
                    class="border border-gray-200 rounded-lg p-4"
                  >
                    <div class="flex justify-between items-start">
                      <div class="flex-1">
                        <!-- Address Types -->
                        <div class="flex flex-wrap gap-2 mb-3">
                          <span 
                            v-if="address.is_invoice_address" 
                            class="badge badge-info"
                          >
                            {{ $t('Rechnungsadresse') }}
                          </span>
                          <span 
                            v-if="address.is_shipping_address" 
                            class="badge badge-success"
                          >
                            {{ address.is_default ? $t('Standard-Lieferadresse') : $t('Lieferadresse') }}
                          </span>
                          <span 
                            v-if="address.is_store" 
                            class="badge badge-warning"
                          >
                            {{ $t('Geschäft') }}
                          </span>
                        </div>
                        
                        <!-- Address Details -->
                        <div class="text-sm text-gray-900" v-html="address.as_html"></div>
                      </div>
                      
                      <!-- Address Actions -->
                      <div class="ml-4 flex flex-col space-y-2">
                        <Link
                          :href="route('editAddress', address.id)"
                          class="btn btn-sonnenglas-lite btn-small text-center"
                        >
                          {{ $t('Bearbeiten') }}
                        </Link>

                        <template v-if="address.is_shipping_address">
                          <Link
                            :href="route('deleteAddress', address.id)"
                            class="btn btn-danger btn-small text-center"
                          >
                            {{ $t('Löschen') }}
                          </Link>
                          <Link
                            :href="route('useAsInvoiceAddress', address.id)"
                            class="btn btn-sonnenglas btn-small text-center"
                          >
                            {{ $t('Als Rechnungsadresse verwenden') }}
                          </Link>
                          <Link
                            v-if="!address.is_default"
                            :href="route('markDefaultShippingAddress', address.id)"
                            class="btn btn-sonnenglas btn-small text-center"
                          >
                            {{ $t('Als Standard-Lieferadresse verwenden') }}
                          </Link>
                        </template>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Users Management Sidebar -->
          <div class="lg:col-span-1">
            <div class="bg-white shadow rounded-lg">
              <div class="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                <h2 class="text-lg font-semibold text-gray-900">{{ $t('Benutzer') }}</h2>
                <Link
                  v-if="currentUser.is_primary"
                  :href="route('inviteUser')"
                  class="btn btn-sonnenglas"
                >
                  <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
                  </svg>
                  {{ $t('Neuen Benutzer hinzufügen') }}
                </Link>
              </div>
              <div class="p-6">
                <!-- Primary user notice -->
                <div v-if="!currentUser.is_primary" class="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div class="flex">
                    <svg class="w-5 h-5 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
                    </svg>
                    <div class="ml-3">
                      <p class="text-sm text-amber-800">
                        {{ $t('Nur der Hauptbenutzer kann Benutzer verwalten.') }}
                      </p>
                    </div>
                  </div>
                </div>

                <div class="space-y-4">
                  <div 
                    v-for="user in customer.users" 
                    :key="user.id"
                    class="relative bg-gray-50 border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow"
                  >
                    <div class="flex items-start justify-between">
                      <div class="flex-1 min-w-0">
                        <div class="flex items-center gap-2 mb-1">
                          <h3 class="text-sm font-semibold text-gray-900 truncate">{{ user.name }}</h3>
                          <span 
                            v-if="user.is_primary" 
                            class="badge badge-primary"
                          >
                            <svg class="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fill-rule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clip-rule="evenodd"/>
                            </svg>
                            {{ $t('Hauptbenutzer') }}
                          </span>
                          <span 
                            v-else-if="user.email === currentUser.email" 
                            class="badge badge-info"
                          >
                            {{ $t('Sie') }}
                          </span>
                        </div>
                        <p class="text-sm text-gray-600 truncate">{{ user.email }}</p>
                        <p class="text-xs text-gray-500 mt-1">
                          {{ user.is_primary ? $t('Kann alle Benutzer verwalten') : $t('Standardbenutzer') }}
                        </p>
                      </div>
                      
                      <!-- Action buttons for primary user -->
                      <div v-if="currentUser.is_primary && !user.is_primary" class="ml-4 flex flex-col gap-2">
                        <!-- Edit button -->
                        <Link
                          :href="route('editUser', user.id)"
                          class="btn btn-sonnenglas-lite btn-small justify-center"
                          title="Benutzer bearbeiten"
                        >
                          <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                          </svg>
                          {{ $t('Bearbeiten') }}
                        </Link>

                        <!-- Remove button -->
                        <button
                          @click="confirmRemoveUser(user)"
                          class="btn btn-danger btn-small justify-center"
                          title="Benutzer entfernen"
                        >
                          <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                          </svg>
                          {{ $t('Entfernen') }}
                        </button>
                      </div>
                      
                      <!-- Display only info for current user -->
                      <div v-else-if="user.email === currentUser.email" class="ml-4">
                        <div class="text-xs text-gray-500 text-right">
                          {{ $t('Dein Konto') }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Empty state when no users -->
                <div v-if="customer.users.length === 0" class="text-center py-6">
                  <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"/>
                  </svg>
                  <h3 class="mt-2 text-sm font-medium text-gray-900">{{ $t('Keine Benutzer') }}</h3>
                  <p class="mt-1 text-sm text-gray-500">{{ $t('Laden Sie Teammitglieder ein, um gemeinsam zu arbeiten.') }}</p>
                </div>

                <!-- Quick actions for primary user -->
                <div v-if="currentUser.is_primary && customer.users.length > 1" class="mt-6 pt-4 border-t border-gray-200">
                  <div class="text-sm text-gray-600">
                    <strong>{{ customer.users.length }}</strong> {{ $t('Benutzer insgesamt') }} •
                    <strong>{{ customer.users.filter(u => u.is_primary).length }}</strong> {{ $t('Hauptbenutzer') }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </AppLayout>
</template>

<script setup lang="ts">
import { getCurrentInstance, inject } from 'vue'
import { Link, router } from '@inertiajs/vue3'
import AppLayout from '@/layouts/AppLayout.vue'
import { toCurrency } from '@/libs/utils'

const instance = getCurrentInstance()
const $t = instance?.appContext.config.globalProperties.$t || ((key: string) => key)
const route = inject<(name?: string, params?: any, absolute?: boolean) => string>('route')

interface Customer {
  id: number
  name: string
  customer_number: string
  country: string
  vat_id: string | null
  email: string
  sorted_addresses: Address[]
  users: User[]
}

interface Address {
  id: number
  is_invoice_address: boolean
  is_shipping_address: boolean
  is_store: boolean
  is_default: boolean
  as_html: string
}

interface User {
  id: number
  name: string
  email: string
  is_primary: boolean
}

interface Country {
  code: string
  name: string
}

interface CustomerOrderStatistics {
  ytdNet: number
  ytdGross: number
  ytdCount: number
  lifetimeNet: number
  lifetimeGross: number
  lifetimeCount: number
  aov: number | null
  currency: string
  year: number
}

interface Props {
  customer: Customer
  missingStoreAddressHint: boolean
  currentUser: User
  isSubscribedToNewsletter: boolean
  countries: Country[]
  statistics?: CustomerOrderStatistics
}

const props = defineProps<Props>()

const getCountryName = (countryCode: string) => {
  const country = props.countries.find(c => c.code === countryCode)
  return country ? country.name : countryCode
}

const formatMoney = (amount: number | null | undefined): string => {
  if (amount === null || amount === undefined) {
    return '—'
  }

  const currency = props.statistics?.currency || 'EUR'
  return toCurrency(amount, currency)
}

const removeUser = (userId: number) => {
  if (!route) {
    console.error('Route helper not available')
    return
  }

  router.delete(route('deleteUser', userId), {
    preserveState: false,
    preserveScroll: false,
    onError: (errors) => {
      console.error('Error removing user:', errors)
      alert($t('Ein Fehler ist beim Entfernen des Benutzers aufgetreten. Bitte versuchen Sie es erneut.'))
    },
    onSuccess: (page) => {
      console.log('User removed successfully')
    }
  })
}

const confirmRemoveUser = (user: User) => {
  const confirmMessage = $t('Bist du sicher, dass du {name} ({email}) aus diesem Konto entfernen möchtest? Dieser Vorgang kann nicht rückgängig gemacht werden.')
    .replace('{name}', user.name)
    .replace('{email}', user.email)

  if (confirm(confirmMessage)) {
    removeUser(user.id)
  }
}
</script>